﻿// REAL numbers for the list pages (Companies, Watchlist, Screens, Compare,
// Alerts, Portfolio, Industry). Nothing is generated here.
// getStockStats(ticker) and getFundamentals(ticker) read from your database
// through GET /api/summaries. The first time a ticker is asked for it is queued
// and fetched in one batch; when the answer arrives the app re-renders.
import { useSyncExternalStore } from "react";
import { API_URL } from "./config.js";

/** "not available": behaves like a number that prints "—" and never compares as up/down */
const NA = Object.freeze({
  toFixed: () => "—",
  toLocaleString: () => "—",
  toString: () => "—",
  valueOf: () => NaN,
});
export const isNA = (v) => v === NA;
const val = (v) => (v === null || v === undefined || Number.isNaN(v) ? NA : v);

const store = new Map();     // ticker -> summary row, or null when the database has no such company
const pending = new Set();
const listeners = new Set();
let version = 0;
let timer = null;

const notify = () => {
  version += 1;
  listeners.forEach((l) => l());
};

async function flush() {
  timer = null;
  const batch = [...pending];
  pending.clear();
  for (let i = 0; i < batch.length; i += 60) {
    const chunk = batch.slice(i, i + 60);
    try {
      const res = await fetch(`${API_URL}/summaries?tickers=${encodeURIComponent(chunk.join(","))}`);
      const rows = res.ok ? await res.json() : [];
      chunk.forEach((t) => store.set(t, null));
      rows.forEach((r) => store.set(r.ticker, r));
    } catch {
      chunk.forEach((t) => store.set(t, null));
    }
  }
  notify();
}

function summary(ticker) {
  const t = String(ticker || "").toUpperCase();
  if (store.has(t)) return store.get(t);
  if (!pending.has(t)) {
    pending.add(t);
    if (!timer) timer = setTimeout(flush, 30);
  }
  return undefined;
}

/** Call once near the top of the app: re-renders everything when new numbers arrive. */
export function useSummariesVersion() {
  return useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => listeners.delete(cb); },
    () => version
  );
}

/** 52 weekly closes, current price, 52-week high / low and 52-week % change */
export function getStockStats(ticker) {
  const s = summary(ticker);
  return {
    series: s ? s.series : [],
    current: val(s && s.price),
    high: val(s && s.high),
    low: val(s && s.low),
    change: val(s && s.change),
  };
}

/** price, P/E, market cap ($B), dividend yield, latest-quarter profit and sales ($M) with YoY change, ROCE */
export function getFundamentals(ticker) {
  const s = summary(ticker);
  return {
    cmp: val(s && s.price),
    pe: val(s && s.pe),
    mcap: val(s && s.mcap),
    div: val(s && s.div),
    np: val(s && s.np),
    npVar: val(s && s.npVar),
    sales: val(s && s.sales),
    salesVar: val(s && s.salesVar),
    roce: val(s && s.roce),
  };
}

export const getPriceSeries = (ticker) => getStockStats(ticker).series;

/** Re-fetch these tickers (numbers update in place, no flicker). */
export function refreshSummaries(tickers) {
  tickers.forEach((t) => t && pending.add(String(t).toUpperCase()));
  if (pending.size && !timer) timer = setTimeout(flush, 30);
}